import React, { useCallback, useId } from 'react';

import SENSITIVE_DATA_CLASS from '@rover/constants/src/platform/sensitiveData.constants';
import { useTheme } from '@rover/kibble/themes';

import { Box, Flex } from '../../../core';
import { FeedbackMessage, PrimaryLabel, SecondaryLabel } from '../sharedTypography';
import { getAriaDescribedByString } from '../utils';

import {
  CharacterCount,
  getBackgroundColor,
  getBorderColor,
  isTextareaVariantOptions,
  TextareaMinHeightType,
  TextFieldCommonProps,
  TextFieldIcon,
  WordCount,
} from './TextField.common';

type BaseWebTextFieldProps = TextFieldCommonProps & {
  describedBy?: string; // this is used to establish relationships between objects for accessibility reasons, to read more: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-describedby
  inputType?: 'text' | 'email' | 'password' | 'url' | 'number';
  primaryLabelA11yHidden?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export type Props = BaseWebTextFieldProps;

const TextField = ({
  describedBy = '',
  disabled,
  fieldError,
  fieldProps,
  fieldWidth = '100%',
  helperMessage,
  icon,
  iconAlignment = 'right',
  marginBottom = '6x',
  placeholder,
  primaryLabel,
  primaryLabelA11yHidden,
  primaryLabelSize,
  readOnly,
  required = true,
  secondaryLabel,
  translatedFieldErrorMessage,
  inputType = 'text',
  onChange = () => {},
  variant = 'default',
  variantOptions = {},
  sensitive = false,
  onFocus = () => {},
  onBlur = () => {},
  onClick = () => {},
  onKeyDown = () => {},
  webInputRef = () => {},
  hideRequiredAsterisk = false,
}: Props): JSX.Element => {
  const theme = useTheme();

  const primaryLabelId = useId();
  const fieldErrorId = useId();
  const helperMessageId = useId();
  const secondaryLabelId = useId();

  const describedByString = getAriaDescribedByString([
    [fieldError, fieldErrorId],
    [helperMessage, helperMessageId],
    [secondaryLabel, secondaryLabelId],
    [describedBy, describedBy],
  ]);

  const handleOnChange = useCallback(
    async (e) => {
      onChange(e);
      fieldProps.onChange(e);
    },
    [onChange, fieldProps]
  );

  const handleOnBlur = useCallback(
    async (e) => {
      onBlur(e);
      fieldProps.onBlur(e);
    },
    [onBlur, fieldProps]
  );

  const refCallback = useCallback(
    (el) => {
      fieldProps.ref(el);
      webInputRef(el);
    },
    [fieldProps, webInputRef]
  );

  let minHeight: TextareaMinHeightType = '12x';
  if (
    variant === 'textarea' &&
    isTextareaVariantOptions(variantOptions) &&
    variantOptions.minHeight
  ) {
    minHeight = variantOptions.minHeight;
  }

  const hasError: boolean = Boolean(fieldError || translatedFieldErrorMessage);

  return (
    <Flex flexDirection="column" mb={marginBottom} flex="1">
      <PrimaryLabel
        required={required}
        primaryLabel={primaryLabel}
        webInputId={primaryLabelId}
        primaryLabelA11yHidden={primaryLabelA11yHidden}
        primaryLabelSize={primaryLabelSize}
        hideRequiredAsterisk={hideRequiredAsterisk}
      />
      <SecondaryLabel secondaryLabel={secondaryLabel} labelId={secondaryLabelId} />
      <Flex
        flex="1"
        flexDirection="row"
        alignItems="center"
        position="relative"
        width={fieldWidth}
        data-testid="kff-text-input-flex-parent"
        className={sensitive ? SENSITIVE_DATA_CLASS : undefined}
      >
        <Box
          aria-describedby={describedByString || undefined} // pass undefined instead of empty string
          aria-disabled={disabled}
          aria-invalid={hasError ? 'true' : 'false'}
          aria-required={required}
          as={variant === 'textarea' ? 'textarea' : 'input'}
          border="2px solid"
          borderColor={getBorderColor(hasError, disabled)}
          borderRadius="secondary"
          color={
            disabled ? theme.colors.text.disabled.toString() : theme.colors.text.primary.toString()
          }
          data-testid="kff-text-input"
          id={primaryLabelId}
          minHeight={minHeight}
          paddingLeft={icon && iconAlignment === 'left' ? '8x' : '3x'}
          paddingRight={icon && iconAlignment === 'right' ? '8x' : '3x'}
          py="2x"
          placeholder={placeholder}
          readOnly={readOnly || disabled}
          type={inputType}
          width="100%"
          sx={{
            background: getBackgroundColor(disabled, readOnly),
            fontSize: theme.text[200].fontSize,
            lineHeight: theme.paragraph[200].lineHeight,
            outlineOffset: '2px',
            pointerEvents: disabled ? 'none' : 'all',
          }}
          {...fieldProps}
          ref={refCallback}
          onChange={handleOnChange}
          onFocus={onFocus}
          onBlur={handleOnBlur}
          onClick={onClick}
          onKeyDown={onKeyDown}
        />
        <TextFieldIcon icon={icon} iconAlignment={iconAlignment} disabled={disabled} />
      </Flex>
      <CharacterCount variant={variant} variantOptions={variantOptions} />
      <WordCount variant={variant} variantOptions={variantOptions} />
      <FeedbackMessage
        fieldError={fieldError}
        fieldErrorId={fieldErrorId}
        helperMessage={helperMessage}
        helperMessageId={helperMessageId}
        translatedFieldErrorMessage={translatedFieldErrorMessage}
      />
    </Flex>
  );
};

export default TextField;
